import { Tooltip } from '@neo4j-ndl/react';
import { CURRENCY, PROJECT_BILLING_METHOD } from '@nx/state';
import { isNotNullish, isNullish } from '@nx/stdlib';
import type { RoundingMode } from 'big.js';
import Big from 'big.js';
import type { ReactElement } from 'react';

// By default we show at most 4 decimal places,
// always rounding up when it comes to dollar amounts
const DEFAULT_MIN_DECIMAL_PLACES = 2;
const DEFAULT_MAX_DECIMAL_PLACES = 4;
export const MONTHLY_MAX_DECIMAL_PLACES = 2;

interface RoundingOptions {
  minimumDecimalPlaces?: number | undefined;
  maximumDecimalPlaces?: number | undefined;
  roundingMode?: RoundingMode | undefined;
}

/**
 * Round to decimal places between min and max.
 * IE shows up to max unless the last decimals places
 * would be 0's then tries to trim them until it hits a
 * non 0 or reaches min.
 *
 * Examples using the default values for min/max:
 *  56.92 -> 56.92
 *  56.9200 -> 56.92
 *  56.9210 -> 56.921
 *  56.9 -> 56.90
 *  56.9215 -> 56.9215
 *  56.92157 -> 56.9216
 *
 * We can't just use Big or JS toFixed cause it pads with 0's
 * and we can't just trim all trialing 0's via casting using Number
 * or parseFloat as stackoverflow would suggest because we
 * need to keep a min number of decimal places, hence this function
 */
const toDecimalPlaces = (num: string | number, options: RoundingOptions): string => {
  const {
    minimumDecimalPlaces = DEFAULT_MIN_DECIMAL_PLACES,
    maximumDecimalPlaces = DEFAULT_MAX_DECIMAL_PLACES,
    roundingMode = Big.roundUp,
  } = options;

  const numberRounded = Big(num).toFixed(maximumDecimalPlaces, roundingMode);

  let bestRounded: string = numberRounded;

  // Iterate through each decimal place until we get a different number and then return.
  // This basically removes trailing 0's a the best moment between max and min decimals places
  for (let i = maximumDecimalPlaces; i >= Math.min(minimumDecimalPlaces, maximumDecimalPlaces); i -= 1) {
    const newRounded = Big(num).toFixed(i, roundingMode);
    // The toNumber.toString basically removes all trailing 0's and then allows us to compare if they are the same
    // IE if the number 56.925 then numberRounded will be 56.9250 and here we'll see that at 4 decimals
    // the number is the same, at 3 decimals the number is the same, and at two decimals the number is different
    // so we just return the number with 3 decimals
    if (Big(newRounded).toNumber().toString() !== Big(bestRounded).toNumber().toString()) {
      return bestRounded;
    }

    bestRounded = newRounded;
  }

  return bestRounded;
};

// Works out at 730 hours on average based on non leap years
const AVERAGE_HOURS_PER_MONTH = (365 / 12) * 24;

const PAUSE_COST_FACTOR = 0.2;

export const getPauseCostHour = (costPerHour: number | string, options: RoundingOptions = {}): string =>
  toDecimalPlaces(Big(costPerHour).mul(PAUSE_COST_FACTOR).toString(), options);

export const getCostMonth = (costPerHour: number | string, options: RoundingOptions = {}) => {
  const defaultTwoDecimalsOptions = {
    ...options,
    ...(isNullish(options.maximumDecimalPlaces) && { maximumDecimalPlaces: MONTHLY_MAX_DECIMAL_PLACES }),
  };
  return toDecimalPlaces(Big(costPerHour).mul(AVERAGE_HOURS_PER_MONTH).toString(), defaultTwoDecimalsOptions);
};

export const getPauseCostMonth = (costPerHour: number | string, options: RoundingOptions = {}) =>
  getCostMonth(getPauseCostHour(costPerHour, options), options);

export const ACUText = () => {
  return (
    <Tooltip type="simple" isPortaled={false}>
      <Tooltip.Trigger>
        <span className="underline decoration-dotted">ACU</span>
      </Tooltip.Trigger>
      <Tooltip.Content>Aura Consumption Units (ACU) will be charged for prepaid billing projects</Tooltip.Content>
    </Tooltip>
  );
};

export const getCurrencyForBillingMethod = (billingMethod: PROJECT_BILLING_METHOD): CURRENCY => {
  if (billingMethod === PROJECT_BILLING_METHOD.PREPAID) {
    return CURRENCY.CREDITS;
  }

  return CURRENCY.DOLLARS;
};

interface AmountFormatOptions {
  maximumDecimalPlaces?: number;
  /**
   * Return only formatted decimal string
   * instead of string with currency specifier
   *
   * IE return 100.01 instead of $100.01
   * or `100.01 ACU`
   */
  hideCurrency?: boolean;

  /**
   * Return only formatted decimal string
   * instead of string with currency specifier
   * when returning ACU values
   *
   * IE return 100.01 instead of `100.01 ACU`
   */
  hideAcuCurrency?: boolean;

  /**
   * Return only formatted decimal string
   * instead of string with currency specifier
   * when returning dollar values
   *
   * IE return 100.01 instead of $100.01
   */
  hideDollarCurrency?: boolean;

  /**
   * Return a string always, not a ReactNode
   */
  textOnly?: boolean;
}

const defaultFormatOptions: AmountFormatOptions = {
  maximumDecimalPlaces: DEFAULT_MAX_DECIMAL_PLACES,
  hideCurrency: false,
  hideAcuCurrency: false,
  hideDollarCurrency: false,
  textOnly: false,
};

export function formatDollars(
  dollarAmount: number | string,
  options: AmountFormatOptions = defaultFormatOptions,
): string {
  const { maximumDecimalPlaces, hideCurrency, hideDollarCurrency } = options;
  const number = dollarAmount === '' ? '0' : dollarAmount;

  const numberRounded = toDecimalPlaces(number, { maximumDecimalPlaces, roundingMode: Big.roundUp });

  if ((isNotNullish(hideCurrency) && hideCurrency) || (isNotNullish(hideDollarCurrency) && hideDollarCurrency)) {
    return numberRounded;
  }

  return `$${numberRounded}`;
}

export function formatACU(dollarAmount: number | string, options: AmountFormatOptions & { textOnly: true }): string;
export function formatACU(dollarAmount: number | string, options?: AmountFormatOptions): ReactElement;
export function formatACU(
  dollarAmount: number | string,
  options: AmountFormatOptions = defaultFormatOptions,
): string | ReactElement {
  const { maximumDecimalPlaces, hideCurrency, hideAcuCurrency } = options;
  const number = dollarAmount === '' ? '0' : dollarAmount;

  const numberRounded = toDecimalPlaces(number, { maximumDecimalPlaces, roundingMode: Big.roundUp });

  if ((isNotNullish(hideCurrency) && hideCurrency) || (isNotNullish(hideAcuCurrency) && hideAcuCurrency)) {
    return numberRounded;
  }

  if (isNotNullish(options.textOnly) && options.textOnly) {
    return `${numberRounded} ACU`;
  }

  return (
    <>
      {numberRounded} <ACUText />
    </>
  );
}

export function formatCost(
  dollarAmount: number | string,
  currency: CURRENCY,
  options: AmountFormatOptions & { textOnly: true },
): string;
export function formatCost(
  dollarAmount: number | string,
  currency: CURRENCY,
  options?: AmountFormatOptions,
): ReactElement;
export function formatCost(
  dollarAmount: number | string,
  currency: CURRENCY,
  options: AmountFormatOptions = defaultFormatOptions,
): string | ReactElement {
  if (currency === CURRENCY.CREDITS) {
    return formatACU(dollarAmount, options);
  }

  return formatDollars(dollarAmount, options);
}
